<template>
  <div class="data-table-container">
    <!-- ページネーション (上部) -->
    <div v-if="showPagination && paginationPosition !== 'bottom'" class="pagination-container">
      <div class="pagination-info">
        全 {{ totalItems }} 件中 {{ startIndex + 1 }}-{{ endIndex + 1 }} 件表示
      </div>
      <div class="pagination-controls">
        <button 
          class="btn btn-sm btn-outline-secondary" 
          :disabled="currentPage <= 1" 
          @click="changePage(currentPage - 1)"
        >
          前へ
        </button>
        <span class="pagination-page">{{ currentPage }} / {{ totalPages }}</span>
        <button 
          class="btn btn-sm btn-outline-secondary" 
          :disabled="currentPage >= totalPages" 
          @click="changePage(currentPage + 1)"
        >
          次へ
        </button>
      </div>
      <div class="per-page-selector">
        <select v-model="localPerPage" @change="changePerPage">
          <option v-for="option in perPageOptions" :key="option" :value="option">
            {{ option }}件/ページ
          </option>
        </select>
      </div>
      
      <!-- 表示設定ボタン -->
      <div v-if="showColumnSelector" class="column-selector-container">
        <button class="btn btn-sm btn-outline-secondary column-selector-button" @click="toggleColumnSelector">
          表示設定
        </button>
        
        <!-- カラム選択ドロップダウン -->
        <div v-if="columnSelectorVisible" class="column-selector-dropdown">
          <div class="column-selector-header">表示する行</div>
          <div class="column-selector-options">
            <div v-for="column in selectableColumns" :key="column.key" class="column-selector-option">
              <input 
                type="checkbox" 
                :id="`column-selector-${column.key}`" 
                :value="column.key" 
                v-model="localVisibleColumns"
                @change="updateVisibleColumns"
              />
              <label :for="`column-selector-${column.key}`">{{ column.label }}</label>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- テーブル -->
    <div class="table-responsive">
      <table class="table">
        <thead>
          <tr>
            <th v-for="column in visibleColumnsFiltered" :key="column.key" :style="{ width: column.width || 'auto' }">
              <slot :name="`header-${column.key}`" :column="column">
                <div class="column-header">
                  {{ column.label }}
                  <span v-if="column.sortable" class="sort-icon">
                    <i class="fa fa-sort" aria-hidden="true"></i>
                  </span>
                </div>
              </slot>
            </th>
            <th v-if="actions && actions.length > 0">操作</th>
          </tr>
        </thead>
        <tbody>
          <template v-if="paginatedItems.length > 0">
            <tr v-for="(item, index) in paginatedItems" :key="getItemKey(item, index)">
              <td v-for="column in visibleColumnsFiltered" :key="column.key">
                <slot :name="column.key" :item="item" :value="item[column.key]">
                  {{ formatValue(item[column.key], column.formatter) }}
                </slot>
              </td>
              <td v-if="actions && actions.length > 0" class="actions-cell">
                <template v-for="(action, actionIndex) in actions" :key="actionIndex">
                  <button 
                    class="btn btn-sm" 
                    :class="action.buttonClass || 'btn-secondary'" 
                    @click="handleAction(action, item)"
                    :disabled="isActionDisabled(action)"
                    :style="{ opacity: isActionDisabled(action) ? 0.3 : 1 }"
                  >
                    <i v-if="action.icon" :class="['fa', action.icon]" aria-hidden="true"></i>
                    {{ action.label }}
                  </button>
                </template>
              </td>
            </tr>
          </template>
          <tr v-else>
            <td :colspan="visibleColumnsFiltered.length + (actions && actions.length > 0 ? 1 : 0)" class="text-center empty-message">
              {{ emptyMessage }}
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <!-- ページネーション (下部) -->
    <div v-if="showPagination && paginationPosition !== 'top'" class="pagination-container">
      <div class="pagination-info">
        全 {{ totalItems }} 件中 {{ startIndex + 1 }}-{{ endIndex + 1}} 件表示
      </div>
      <div class="pagination-controls">
        <button 
          class="btn btn-sm btn-outline-secondary" 
          :disabled="currentPage <= 1" 
          @click="changePage(currentPage - 1)"
        >
          前へ
        </button>
        <span class="pagination-page">{{ currentPage }} / {{ totalPages }}</span>
        <button 
          class="btn btn-sm btn-outline-secondary" 
          :disabled="currentPage >= totalPages" 
          @click="changePage(currentPage + 1)"
        >
          次へ
        </button>
      </div>
      <div class="per-page-selector">
        <select v-model="localPerPage" @change="changePerPage">
          <option v-for="option in perPageOptions" :key="option" :value="option">
            {{ option }}件/ページ
          </option>
        </select>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'FormDataTable',
  props: {
    /**
     * テーブルに表示するデータ配列
     */
    items: {
      type: Array,
      required: true,
      default: () => []
    },
    /**
     * 表示するカラム定義
     * [{ key: 'id', label: 'ID', width: '50px', formatter: (value) => `#${value}` }]
     */
    columns: {
      type: Array,
      required: true,
      default: () => []
    },
    /**
     * 操作ボタン定義
     * [{ 
     *   label: '詳細', 
     *   buttonClass: 'btn-secondary', 
     *   icon: 'fa-info-circle', // FontAwesomeアイコン
     *   action: (item) => {}, 
     *   condition: (item) => true // ボタン表示条件
     * }]
     */
    actions: {
      type: Array,
      default: () => []
    },
    /**
     * ページあたりの表示件数
     */
    perPage: {
      type: Number,
      default: 10
    },
    /**
     * ページネーションオプション
     */
    perPageOptions: {
      type: Array,
      default: () => [10, 20, 50, 100]
    },
    /**
     * 現在のページ番号（1から始まる）
     */
    page: {
      type: Number,
      default: 1
    },
    /**
     * ページネーション表示位置 ('top', 'bottom', 'both')
     */
    paginationPosition: {
      type: String,
      default: 'bottom',
      validator: (value) => ['top', 'bottom', 'both'].includes(value)
    },
    /**
     * ページネーション表示有無
     */
    showPagination: {
      type: Boolean,
      default: true
    },
    /**
     * データが空の場合のメッセージ
     */
    emptyMessage: {
      type: String,
      default: 'データがありません'
    },
    /**
     * 一意のキーとして使用するプロパティ名
     */
    itemKey: {
      type: String,
      default: 'id'
    },
    /**
     * カラム選択機能の表示有無
     */
    showColumnSelector: {
      type: Boolean,
      default: false
    },
    /**
     * 表示するカラムのキー配列
     */
    visibleColumns: {
      type: Array,
      default: () => []
    }
  },
  data() {
    return {
      currentPage: this.page,
      localPerPage: this.perPage,
      columnSelectorVisible: false,
      localVisibleColumns: 0 < this.visibleColumns.length ? [...this.visibleColumns] : this.columns.map(col => col.key)
    }
  },
  computed: {
    /**
     * 合計アイテム数
     */
    totalItems() {
      return this.items.length;
    },
    /**
     * 合計ページ数
     */
    totalPages() {
      return Math.ceil(this.totalItems / this.localPerPage) || 1;
    },
    /**
     * 現在のページの開始インデックス
     */
    startIndex() {
      return (this.currentPage - 1) * this.localPerPage;
    },
    /**
     * 現在のページの終了インデックス
     */
    endIndex() {
      const end = this.startIndex + this.localPerPage;
      return Math.min(end, this.totalItems) - (this.totalItems === 0 ? 0 : 1);
    },
    /**
     * 現在のページのアイテム
     */
    paginatedItems() {
      if (!this.showPagination) {
        return this.items;
      }
      const start = this.startIndex;
      const end = Math.min(start + this.localPerPage, this.totalItems);
      return this.items.slice(start, end);
    },
    /**
     * 表示するカラム
     */
    visibleColumnsFiltered() {
      return this.columns.filter(column => this.localVisibleColumns.includes(column.key));
    },
    /**
     * 選択可能なカラム（選択UIに表示するカラム）
     */
    selectableColumns() {
      // 'select'キーのカラムは表示設定から除外（常に表示）
      return this.columns.filter(column => column.key !== 'select');
    }
  },
  watch: {
    page(newPage) {
      this.currentPage = newPage;
    },
    perPage(newPerPage) {
      this.localPerPage = newPerPage;
    },
    totalPages(newPages) {
      // ページ数が減少した場合、現在のページが範囲外なら調整
      if (this.currentPage > newPages) {
        this.currentPage = newPages;
      }
    },
    visibleColumns(newColumns) {
      this.localVisibleColumns = [...newColumns];
    },
    columns: {
      handler(newColumns) {
        // カラムが変更された場合、デフォルトですべて表示
        if (this.visibleColumns.length === 0) {
          this.localVisibleColumns = newColumns.map(col => col.key);
        }
      },
      immediate: true
    }
  },
  methods: {
    /**
     * アイテムの一意のキーを取得
     */
    getItemKey(item, index) {
      return item[this.itemKey] !== undefined ? item[this.itemKey] : index;
    },
    /**
     * 値をフォーマット
     */
    formatValue(value, formatter) {
      if (formatter && typeof formatter === 'function') {
        return formatter(value);
      }
      return value;
    },
    /**
     * ページを変更
     */
    changePage(page) {
      if (page < 1 || page > this.totalPages) {
        return;
      }
      this.currentPage = page;
      this.$emit('page-change', page);
    },
    /**
     * 表示件数を変更
     */
    changePerPage() {
      // 表示件数変更時、ページ位置を調整
      const newStartIndex = Math.floor(this.startIndex / this.localPerPage) * this.localPerPage;
      this.currentPage = Math.floor(newStartIndex / this.localPerPage) + 1;
      
      this.$emit('per-page-change', this.localPerPage);
    },
    /**
     * アクションボタンのクリックハンドラ
     */
    handleAction(action, item) {
      if (action.action && typeof action.action === 'function') {
        action.action(item);
      }
      this.$emit('action', { action: action.key || action.label, item });
    },
    /**
     * アクションボタンの表示判定
     */
    showAction(action, item) {
      if (action.condition && typeof action.condition === 'function') {
        return action.condition(item);
      }
      return true;
    },
    /**
     * カラム選択ドロップダウンの表示切替
     */
    toggleColumnSelector() {
      this.columnSelectorVisible = !this.columnSelectorVisible;
    },
    /**
     * 表示カラム更新時の処理
     */
    updateVisibleColumns() {
      // 'select'カラムは常に表示
      if (!this.localVisibleColumns.includes('select')) {
        this.localVisibleColumns.unshift('select');
      }
      
      // 親コンポーネントに変更を通知
      this.$emit('visible-columns-change', this.localVisibleColumns);
    },
    /**
     * アクションボタンが無効かどうかを判定
     */
    isActionDisabled(action) {

      if (action.disabled !== undefined && action.disabled !== null) {
        if (typeof action.disabled === 'function') {
          return action.disabled();
        }
        return !!action.disabled;
      }
    },
  }
}
</script>

<style scoped>
.data-table-container {
  background-color: white;
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  padding: 1.5rem;
}

.table {
  width: 100%;
  border-collapse: collapse;
}

.table th,
.table td {
  padding: 0.75rem;
  text-align: left;
  border-bottom: 1px solid var(--gray-medium);
}

.table th {
  font-weight: bold;
  background-color: #195983;
  color: white;
  &:first-child {
    border-top-left-radius: 4px;
    border-bottom-left-radius: 4px;
  }
  &:last-child {
    border-top-right-radius: 4px;
    border-bottom-right-radius: 4px;
  }
}

.table-responsive {
  overflow-x: auto;
  margin-bottom: 1rem;
}

.pagination-container {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1rem;
  font-size: 0.875rem;
}

.pagination-controls {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.pagination-page {
  padding: 0 0.5rem;
}

.per-page-selector select {
  padding: 0.25rem;
  border-radius: 4px;
  border: 1px solid var(--gray-medium);
}

.actions-cell {
  vertical-align: middle;
}

/* Add padding between action buttons */
.actions-cell button {
  margin-right: 0.5rem;
}

/* Remove margin for the last button */
.actions-cell button:last-child {
  margin-right: 0;
}

.btn-sm {
  padding: 0.25rem 0.5rem;
  font-size: 0.875rem;
}

.empty-message {
  padding: 1.5rem;
  font-style: italic;
  color: var(--gray-dark, #6c757d);
}

.column-selector-container {
  position: relative;
  margin-left: auto;
}

.column-selector-button {
  white-space: nowrap;
}

.column-selector-dropdown {
  position: absolute;
  top: 100%;
  right: 0;
  width: 200px;
  background-color: white;
  border: 1px solid #ddd;
  border-radius: 4px;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
  z-index: 1000;
  padding: 10px;
  margin-top: 5px;
}

.column-selector-header {
  font-weight: bold;
  padding-bottom: 8px;
  border-bottom: 1px solid #eee;
  margin-bottom: 8px;
}

.column-selector-options {
  max-height: 300px;
  overflow-y: auto;
}

.column-selector-option {
  display: flex;
  align-items: center;
  padding: 5px 0;
}

.column-selector-option label {
  margin-left: 8px;
  cursor: pointer;
}
</style>
